import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import ROUTES from '@src/constants/route';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import {
  PACKAGE_CODE_V3,
  FREE_PACKAGE_CODES,
  PACKAGE_CHANGE_TYPES,
  PACKAGE_LEVEL,
  ORDER_STATUS,
  PACKAGE_TYPE,
} from '@src/constants/package';
import {
  checkCustomPackage,
  getPackageChangeType,
} from '@src/services/package';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';
import camelcaseKeys from 'camelcase-keys';
import { checkVietNam } from '@src/utils/checkCountry';
import apis from '@src/apis';
import { trackFreePlanActivated } from '@src/services/moengage';
import useHandleOrderError from '@src/hooks/useHandleOrderError';
import actions from '@src/redux/actions';
import { delay } from '@src/utils/delay';
import NotificationDialog from '@src/components/NotificationDialog';
import ProcessHandler from '@src/components/ProcessHandler';

const ButtonBuyPackage = ({ data, packages, isHighlightPackage }) => {
  const location = useLocation();
  const history = useHistory();
  const { voucherCode, bannerType } = camelcaseKeys(
    queryString.parse(location.search) || {},
  );
  const { t } = useTranslation();
  const isVietNam = checkVietNam();
  const [openDialog, setOpenDialog] = useState(false);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState({
    createFreeOrder: false,
    cancelOrder: false,
  });
  const optionsSelected = useSelector(
    (state) => state.packages.optionsSelected,
  );

  const { usingPackage, apiUsageOptions, packageExpiryDate, latestOrder } =
    useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);

  const apiUsingPackage = packages?.find(
    (pack) => pack?.code === apiUsageOptions?.code,
  );

  const isUpgradePackage = useMemo(() => {
    const determinePackageChange = () => {
      if (!usingPackage) return null;
      return getPackageChangeType({
        currPackage: data,
        prevPackage: apiUsingPackage || usingPackage,
        prevPackageExpiredDate: apiUsingPackage
          ? apiUsageOptions?.packageExpiryDate
          : packageExpiryDate,
      });
    };
    return determinePackageChange() === PACKAGE_CHANGE_TYPES.UPGRADE;
  }, [data, usingPackage, apiUsingPackage, apiUsageOptions, packageExpiryDate]);

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const handleCancelPendingOrder = async () => {
    if (latestOrder.status !== ORDER_STATUS.PENDING) return;

    setLoading({ ...loading, cancelOrder: true });
    const orderData = await apis.orders.cancelOrder(latestOrder.id);
    setLoading({ ...loading, cancelOrder: false });
    if (!orderData.status)
      dispatch(actions.noti.push({ severity: 'error', code: orderData.code }));
    else {
      if (data.result.type === PACKAGE_TYPE.STUDIO)
        dispatch(actions.user.getLatestOrderSuccess(data.result));
      else if (data.result.type === PACKAGE_TYPE.DUBBING)
        dispatch(actions.user.getLatestDubbingOrderSuccess(data.result));

      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'cancelOrderSuccessfully',
        }),
      );
    }

    setOpenDialog(false);
    setLoading({ ...loading, createFreeOrder: false });
  };

  const handleContinuePayingDialog = () => {
    if (latestOrder.status !== ORDER_STATUS.PENDING) return;
    if (latestOrder.paymentLink)
      window.location.assign(latestOrder.paymentLink);
    else history.replace({ search: `?package=${data.code}` });
  };

  const isExpiredPackage = checkExpiredDate(user?.packageExpiryDate);

  const isCustomPackage = checkCustomPackage({
    type: data.type,
    level: data.level,
  });
  const renderButtonTextV3 = () => {
    if (isCustomPackage) {
      return t('buyNow');
    }

    const isUsingV3Package = Object.values(PACKAGE_CODE_V3)?.includes(
      usingPackage?.code,
    );
    const isUsingFreePkg = FREE_PACKAGE_CODES.includes(usingPackage?.code);

    if (FREE_PACKAGE_CODES.includes(data.code))
      return isUsingFreePkg ? t('using') : t('activeNow');

    const hasSamePackage =
      usingPackage?.expiresIn === data?.expiresIn &&
      usingPackage?.level === data?.level;
    if (
      hasSamePackage ||
      isUpgradePackage ||
      isUsingFreePkg ||
      isExpiredPackage ||
      !isUsingV3Package
    )
      return t('buyNow');

    return t('buyPackageBtnTitle');
  };

  const checkDisplayBuyButton = (studioPackages) => {
    const basicTrialStudioPackages = [PACKAGE_LEVEL.TRIAL];

    return (
      data.price > 0 ||
      !studioPackages?.id ||
      basicTrialStudioPackages.includes(studioPackages?.level)
    );
  };
  const displayBuyButton = checkDisplayBuyButton(usingPackage);

  const handleBuyPackageCustom = () => {
    const params = new URLSearchParams();
    if (data?.code) params.set('package', data?.code);
    if (voucherCode) params.set('voucherCode', voucherCode);
    if (optionsSelected?.credits)
      params.set('credits', optionsSelected?.credits);
    if (optionsSelected?.devices != null && optionsSelected?.devices >= 0)
      params.set('devices', optionsSelected?.devices);
    if (
      optionsSelected?.voiceClonings != null &&
      optionsSelected?.voiceClonings >= 0
    )
      params.set('voiceClonings', optionsSelected?.voiceClonings);
    if (
      optionsSelected?.concurrentRequestApi != null &&
      optionsSelected?.concurrentRequestApi >= 0
    )
      params.set('concurrentRequestApi', optionsSelected?.concurrentRequestApi);

    const paymentUrl = `${ROUTES.PAYMENT}?${params.toString()}`;

    history.push(paymentUrl);
  };

  // Create free order
  const isUsingFreePackage =
    [PACKAGE_LEVEL.BASIC, PACKAGE_LEVEL.DUBBING_BASIC].includes(data.level) &&
    usingPackage.code === data.code;

  const handleCreateFreeOrder = async (packageId) => {
    if (usingPackage.level === PACKAGE_LEVEL.BASIC || isUsingFreePackage)
      return;

    if (latestOrder.status === ORDER_STATUS.PENDING) {
      setOpenDialog(true);
      return;
    }

    setLoading({ ...loading, createFreeOrder: true });
    const orderData = await apis.orders.createOrder({
      packageId,
      isGlobal: !isVietNam,
      downgradePackage: true,
    });
    setLoading({ ...loading, createFreeOrder: false });

    if (!orderData?.status) {
      // Check for error code 6001 and show popup
      if (useHandleOrderError(orderData)) {
        return;
      }
    }

    if (orderData.status) {
      trackFreePlanActivated({
        previousPackageCode: usingPackage?.code,
      });
      dispatch(actions.user.getLatestOrderSuccess(orderData.result));
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'buyPackageSuccessfully',
        }),
      );

      dispatch(actions.user.getUsingPackageSuccess(orderData.result.package));
      history.push(ROUTES.TTS);

      return;
    }

    dispatch(
      actions.noti.push({ severity: 'error', message: 'buyPackageFailure' }),
    );
  };

  const handleBuyPackage = async (topUpPackageCode = '') => {
    if (data.price === 0) {
      await delay(1000);
      handleCreateFreeOrder(data.id);
      return;
    }

    let packageCode = data?.code;
    if (topUpPackageCode) {
      packageCode = topUpPackageCode;
    }

    const buildSearchParams = (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      return `?${searchParams.toString()}`;
    };

    const baseParams = {
      package: packageCode,
      creditsPerCycle:
        data?.cycle && !topUpPackageCode
          ? data?.cycle?.creditsPerCycle
          : undefined,
    };

    const search =
      voucherCode && !topUpPackageCode
        ? buildSearchParams({
            ...baseParams,
            voucherCode,
            bannerType,
            packageCode: data?.code,
          })
        : buildSearchParams({
            ...baseParams,
          });

    history.push(`${ROUTES.PAYMENT}${search}`);
  };

  return (
    <>
      <Button
        fullWidth
        className={classNames('button-start', {
          'button-highlight':
            isHighlightPackage || data.level === PACKAGE_LEVEL.SPECIAL,
          'button-disabled': isCustomPackage
            ? false
            : !displayBuyButton && !isExpiredPackage,
        })}
        variant={
          data.level === PACKAGE_LEVEL.SPECIAL ? 'contained' : 'outlined'
        }
        data-id="add-to-cart"
        data-product={data?.type}
        data-package-code={data?.code}
        data-pricing-duration={data?.duration}
        data-button-text={renderButtonTextV3()}
        onClick={() => {
          if (isCustomPackage) {
            handleBuyPackageCustom();
          } else {
            handleBuyPackage(null);
          }
        }}
        disabled={
          isCustomPackage ? false : !displayBuyButton && !isExpiredPackage
        }
      >
        {renderButtonTextV3()}
      </Button>
      {data.price === 0 && (
        <NotificationDialog
          name="pendingOrder"
          title={t('havePendingOrderTitle')}
          description={t('hasOrderWaitForConfirmTransferDescription')}
          variant="warning"
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          actionComponent={
            <>
              <Button variant="outlined" onClick={handleCancelPendingOrder}>
                <ProcessHandler loading={loading.cancelOrder} size={25}>
                  {t('cancelOrder')}
                </ProcessHandler>
              </Button>
              <Button variant="contained" onClick={handleContinuePayingDialog}>
                {t('continue')}
              </Button>
            </>
          }
        />
      )}
    </>
  );
};

export default ButtonBuyPackage;
